import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Line } from "@react-three/drei";
import { observer } from "mobx-react-lite";
import DraggableObject from "./DraggableObject";
import { SelectableSymbol, BoundingBox } from "./SelectableSymbol";
import { TurnStairsType, zIndexEpsilon } from "../../types/floorplannerTypes";
import { FloorplannerStoreContext } from "../../store/floorplannerStore";
import { editorStore } from "../../store/editorStore";
import { canvasAccentColor } from "../../constants/brandColors";
import {
  createTurnStair3DSteps,
  getTurnStairsPlanBounds,
} from "../../features/floorplan3d/symbols/primitives/stairs/stairGeometry";
import { planDashedLineProps } from "../../utils/planCutPlane";

interface TurnStairsProps {
  turnStair: TurnStairsType;
  onDragStart: (turnStair: TurnStairsType, offset: [number, number]) => void;
  onDrag: (newPosition: [number, number]) => void;
  onDragEnd: (endPosition: [number, number]) => void;
}

/**
 * 2D plan view for L/U turn stairs.
 * Sizing is panel-driven; draws flight/landing outlines from the shared geometry builder.
 */
const TurnStairs: React.FC<TurnStairsProps> = observer(
  ({ turnStair, onDragStart, onDrag, onDragEnd }) => {
    const groupRef = useRef<THREE.Group>(null);
    const floorplannerStore = React.useContext(FloorplannerStoreContext);
    const lineWeight = turnStair.lineWeight || floorplannerStore.symbolLineWeight;
    const lineColor = turnStair.lineColor || floorplannerStore.lineColor;
    const [isHovered, setIsHovered] = useState(false);
    const [localPosition, setLocalPosition] = useState<[number, number]>([
      turnStair.position.x,
      turnStair.position.y,
    ]);
    const flipX = turnStair.flipHorizontal ? -1 : 1;
    const flipY = turnStair.flipVertical ? -1 : 1;
    const centerPosition: [number, number] = [0, 0];
    const pivot: [number, number] = [0, 0];
    const noopRaycast = () => null;
    const dashedLineProps = planDashedLineProps(turnStair);

    const geometryOptions = useMemo(
      () => ({
        defaultStepSize: floorplannerStore.stairStepSize,
      }),
      [floorplannerStore.stairStepSize],
    );

    const planBounds = useMemo(
      () => getTurnStairsPlanBounds(turnStair, geometryOptions),
      [
        geometryOptions,
        turnStair,
        turnStair.stairShape,
        turnStair.turnDirection,
        turnStair.stairWidth,
        turnStair.firstFlightSteps,
        turnStair.secondFlightSteps,
        turnStair.landingDepth,
        turnStair.stairStepSize,
      ],
    );

    const stepOutlines = useMemo(() => {
      const steps = createTurnStair3DSteps(turnStair, geometryOptions);
      return steps.map((step) => {
        const points = step.shape.getPoints();
        if (points.length === 0) return [] as THREE.Vector3[];
        const closed = [...points, points[0]];
        return closed.map(
          (point) => new THREE.Vector3(point.x * flipX, point.y * flipY, 0),
        );
      });
    }, [
      flipX,
      flipY,
      geometryOptions,
      turnStair,
      turnStair.stairShape,
      turnStair.turnDirection,
      turnStair.stairWidth,
      turnStair.firstFlightSteps,
      turnStair.secondFlightSteps,
      turnStair.landingDepth,
      turnStair.stairStepSize,
    ]);

    const calculateBoundingBox = useCallback((): BoundingBox => {
      const lineWeightAdjustment = lineWeight / 100;
      const width = planBounds.width + lineWeightAdjustment;
      const height = planBounds.depth + lineWeightAdjustment;
      const halfWidth = width / 2;
      const halfHeight = height / 2;
      return {
        topLeft: [-halfWidth, halfHeight],
        topRight: [halfWidth, halfHeight],
        bottomLeft: [-halfWidth, -halfHeight],
        bottomRight: [halfWidth, -halfHeight],
        width,
        height,
        depth: 0,
      };
    }, [lineWeight, planBounds.depth, planBounds.width]);

    useEffect(() => {
      editorStore.updateGroupRef(turnStair.id, groupRef.current);
    }, [turnStair.id]);

    useEffect(() => {
      setLocalPosition([turnStair.position.x, turnStair.position.y]);
    }, [turnStair.position.x, turnStair.position.y]);

    return (
      <group position={[0, 0, turnStair.zIndex * zIndexEpsilon]}>
        <DraggableObject
          position={localPosition}
          onDragStart={(offset) => onDragStart(turnStair, offset)}
          onDragEnd={(endPosition) => onDragEnd(endPosition)}
          onDrag={onDrag}
          selectable={true}
          attachmentId={turnStair.id}
          attachmentType="noAttachments"
          symbol={turnStair}
        >
          <SelectableSymbol
            ref={groupRef}
            handleSize={floorplannerStore.symbolHandleSize}
            calculateBoundingBox={calculateBoundingBox}
            onResize={() => {
              // Panel-driven sizing for turn stairs; no interactive resize handles.
            }}
            center={centerPosition}
            rotation={turnStair.rotation}
            symbol={turnStair}
            drawHandles={false}
            pivot={pivot}
            onPointerOver={() => setIsHovered(true)}
            onPointerOut={() => setIsHovered(false)}
          >
            <group
              ref={groupRef}
              rotation={[0, 0, turnStair.rotation || 0]}
              onPointerEnter={() => {
                if (
                  document.body.style.cursor !== "grabbing" &&
                  document.body.style.cursor !== "move"
                ) {
                  document.body.classList.add("cursor-vector");
                }
              }}
              onPointerLeave={() => {
                if (
                  document.body.style.cursor !== "grabbing" &&
                  document.body.style.cursor !== "move"
                ) {
                  document.body.classList.remove("cursor-vector");
                }
              }}
            >
              <mesh position={[0, 0, -0.03]}>
                <boxGeometry args={[planBounds.width, planBounds.depth, 0.06]} />
                <meshBasicMaterial transparent opacity={0} />
              </mesh>
              {stepOutlines.map((points, index) =>
                points.length >= 2 ? (
                  <Line
                    key={`${turnStair.id}-outline-${index}`}
                    points={points.map((point) => point.toArray() as [number, number, number])}
                    color={canvasAccentColor(
                      turnStair.selected,
                      !!isHovered,
                      lineColor,
                    )}
                    lineWidth={lineWeight}
                    {...dashedLineProps}
                    raycast={noopRaycast}
                  />
                ) : null,
              )}
            </group>
          </SelectableSymbol>
        </DraggableObject>
      </group>
    );
  },
);

export default TurnStairs;
